import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Link,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/MergeType';
import { formatDistanceToNow, parseJSON } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  useUpdateSupportCase,
  useInternalSupportAgents,
  useInternalSupportCase,
} from '../../../data/support/useSupport';
import {
  useInternalAccount,
  useInternalAccounts,
} from '../../../data/useInternalAdmin';
import type { InternalAccount } from '../../../data/useInternalAdmin';
import {
  SupportCase,
  SupportCaseStatus,
  SupportCasePriority,
} from '../../../types/support.types';
import CaseStatusChip from './CaseStatusChip';
import MergeCaseDialog from './MergeCaseDialog';
import SLAIndicator from './SLAIndicator';

const STATUS_OPTIONS: SupportCaseStatus[] = [
  'open',
  'pending',
  'waiting_on_customer',
  'resolved',
  'closed',
];

const PRIORITY_OPTIONS: SupportCasePriority[] = [
  'low',
  'medium',
  'high',
  'critical',
];

interface SupportCaseSidebarProps {
  supportCase: SupportCase;
}

const SupportCaseSidebar: React.FC<SupportCaseSidebarProps> = ({
  supportCase,
}) => {
  const navigate = useNavigate();
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const updateCase = useUpdateSupportCase(supportCase.id);
  const agentsQuery = useInternalSupportAgents();
  const agents = agentsQuery.data || [];
  const accountQuery = useInternalAccount(supportCase.account_id);
  const accountData = accountQuery.data?.data;
  const accountsQuery = useInternalAccounts({ size: 200 });
  const accounts = accountsQuery.data?.data || [];
  const mergedIntoQuery = useInternalSupportCase(
    supportCase.merged_into_case_id || '',
  );
  const mergedIntoCase = mergedIntoQuery.data;

  const handleStatusChange = (status: SupportCaseStatus) => {
    updateCase.mutate({ status });
  };

  const handlePriorityChange = (priority: SupportCasePriority) => {
    updateCase.mutate({ priority });
  };

  const handleAssigneeChange = (userId: string) => {
    updateCase.mutate({ assignee_user_id: userId || null });
  };

  const handleAccountChange = (account: InternalAccount | null) => {
    if (account) {
      updateCase.mutate({ account_id: account.id });
    }
  };

  const selectedAccount =
    accounts.find((a) => a.id === supportCase.account_id) || null;

  return (
    <Box>
      {/* Case Info */}
      <Paper
        elevation={0}
        sx={{ p: 2, border: 1, borderColor: 'divider', mb: 2 }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Case Info
        </Typography>

        <TextField
          label="Status"
          fullWidth
          size="small"
          select
          value={supportCase.status}
          onChange={(e) =>
            handleStatusChange(e.target.value as SupportCaseStatus)
          }
          sx={{ mb: 2 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              <CaseStatusChip value={s} type="status" />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Priority"
          fullWidth
          size="small"
          select
          value={supportCase.priority}
          onChange={(e) =>
            handlePriorityChange(e.target.value as SupportCasePriority)
          }
          sx={{ mb: 2 }}
        >
          {PRIORITY_OPTIONS.map((p) => (
            <MenuItem key={p} value={p}>
              <CaseStatusChip value={p} type="priority" />
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Assignee"
          fullWidth
          size="small"
          select
          value={supportCase.assignee_user_id || ''}
          onChange={(e) => handleAssigneeChange(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="">
            <Typography variant="body2" color="text.secondary">
              Unassigned
            </Typography>
          </MenuItem>
          {agents.map((agent) => (
            <MenuItem key={agent.id} value={agent.id}>
              {agent.display_name || agent.email}
            </MenuItem>
          ))}
        </TextField>

        <Box>
          <Typography variant="caption" color="text.secondary">
            SLA
          </Typography>
          <SLAIndicator
            createdAt={supportCase.created_at}
            status={supportCase.status}
            priority={supportCase.priority}
          />
        </Box>
      </Paper>

      {/* Account Info */}
      <Paper
        elevation={0}
        sx={{ p: 2, border: 1, borderColor: 'divider', mb: 2 }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Account
        </Typography>
        <Autocomplete
          options={accounts}
          getOptionLabel={(opt) =>
            opt.attributes.title
              ? `${opt.attributes.name} (${opt.attributes.title})`
              : opt.attributes.name
          }
          value={selectedAccount}
          onChange={(_, value) => handleAccountChange(value)}
          loading={accountsQuery.isLoading}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
          renderInput={(params) => (
            <TextField {...params} label="Account" size="small" />
          )}
          sx={{ mb: 1 }}
        />
        {supportCase.account_id && (
          <Link
            component="button"
            variant="caption"
            onClick={() =>
              navigate(`/internal/accounts/${supportCase.account_id}`)
            }
          >
            View account
          </Link>
        )}
      </Paper>

      {/* Creator Info */}
      <Paper
        elevation={0}
        sx={{ p: 2, border: 1, borderColor: 'divider', mb: 2 }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Requester
        </Typography>
        <Typography variant="body2">
          {supportCase.creator?.display_name ||
            supportCase.creator?.email ||
            supportCase.creator_type}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Created{' '}
          {formatDistanceToNow(parseJSON(supportCase.created_at), {
            addSuffix: true,
          })}
        </Typography>
      </Paper>

      {/* Merge */}
      <Paper
        elevation={0}
        sx={{ p: 2, border: 1, borderColor: 'divider', mb: 2 }}
      >
        {supportCase.merged_into_case_id ? (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Merged
            </Typography>
            <Link
              component="button"
              variant="body2"
              onClick={() =>
                navigate(
                  `/internal/support/cases/${supportCase.merged_into_case_id}`,
                )
              }
            >
              Merged into #{mergedIntoCase?.case_number ?? '...'}
            </Link>
          </>
        ) : (
          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<MergeIcon />}
            onClick={() => setMergeOpen(true)}
          >
            Merge Case
          </Button>
        )}
      </Paper>

      <MergeCaseDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        sourceCase={supportCase}
      />
    </Box>
  );
};

export default SupportCaseSidebar;
